import { Badge, Box, Button, Card, Flex, Text } from '@chakra-ui/react';
import { createDataCollectionContext, dbTimeToUserTime, priceFormatter, useMobileContext, useValidatedSorting } from '@shared';
import { createColumnHelper } from '@tanstack/react-table';
import ordersService from '../../../api/services/ordersService';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useMeta } from '../../../api/hooks/useMeta';

const columnHelper = createColumnHelper<Order>();
const TIMEZONE = import.meta.env.VITE_DEFAULT_TIMEZONE as string;

export default function OrderHistory() {
	const { DataCollectionContextProvider, DataCollectionTable, DataCollectionTableFooter } = useMemo(() => createDataCollectionContext<Order>(), []);
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { meta } = useMeta('ordersService');
	const { isMobile } = useMobileContext();

	function getPaymentStatusBadgeColor(paymentStatus: PaymentStatus): string {
		switch (paymentStatus) {
			case 'paid':
				return 'rgb(61, 213, 152)';
			case 'awaitingPayment':
				return 'yellow.400';
			case 'awaitingInvoiceCreation':
				return 'yellow.400';
			case 'cancelled':
				return 'red.400';
			default:
				return 'grey.500';
		}
	}

	const columns = [
		columnHelper.accessor('timeCreated', {
			header: t('general.time_created'),
			sortingFn: 'basic',
			cell: ({ row }) => {
				return <Text>{dbTimeToUserTime(row.original.timeCreated, TIMEZONE)}</Text>;
			},
		}),
		columnHelper.accessor(row => row.orderRows?.[0]?.name, {
			id: 'orderRows',
			header: t('general.product_info'),
			enableSorting: false,
			cell: ({ row }) => {
				return <Text>{row.original.orderRows?.find(or => or.SKU === 'SMS')?.name}</Text>;
			},
		}),
		columnHelper.accessor('paymentMethod', {
			header: t('general.payment_method'),
			sortingFn: 'basic',
			cell: ({ row }) => {
				return <Text>{meta?.paymentMethod?.[row.original.paymentMethod]}</Text>;
			},
		}),
		columnHelper.accessor('paymentStatus', {
			header: t('general.payment_status'),
			sortingFn: 'basic',
			cell: ({ row }) => {
				return (
					<Badge backgroundColor={getPaymentStatusBadgeColor(row.original.paymentStatus)}>
						{meta?.paymentStatus?.[row.original.paymentStatus] || t('general.unknown')}
					</Badge>
				);
			},
		}),
		columnHelper.accessor('totalWithoutVat', {
			header: t('general.total_without_vat'),
			sortingFn: 'basic',
			cell: ({ row }) => {
				if (!row.original.totalWithoutVat) {
					return '-';
				}
				return <Text>{priceFormatter(row.original.totalWithoutVat)}</Text>;
			},
		}),

		columnHelper.accessor('totalWithVat', {
			header: t('general.total'),
			sortingFn: 'basic',
			cell: ({ row }) => {
				if (!row.original.totalWithVat) {
					return '-';
				}
				return <Text>{priceFormatter(row.original.totalWithVat)}</Text>;
			},
		}),
		columnHelper.display({
			id: 'orderID',
			cell: ({ row }) => {
				return (
					<Button data-cy="openReceiptButton" onClick={() => navigate(`/orders/${row.original.orderID}`)} size="xs">
						{t('general.open_order')}
					</Button>
				);
			},
		}),
	];

	useValidatedSorting('orders', columns);

	return (
		<Card.Root flex={1} display="flex">
			<Card.Body display="flex" flexDirection="column" padding={5} gap={5} overflow={isMobile ? 'initial' : 'auto'}>
				<DataCollectionContextProvider
					dataKey="orders"
					keyAttribute="orderID"
					emptyDataText={t('general.no_orders_found')}
					fetchFn={(...args) => ordersService.getAllResources(...args)}
					queryParams={{ getRelated: 'orderRows' }}
					initialItemsPerPage={25}
					columns={columns}
					defaultHiddenColumns={[]}
					isServerSide={true}
					isSelectable={false}
					defaultSort={{ id: 'timeCreated', desc: true }}
					onClickRow={row => {
						navigate(`/orders/${row.original.orderID}`);
					}}
				>
					<Box flex={1} overflow="auto" minH={0}>
						<DataCollectionTable />
					</Box>
					<Flex gap={2} flexShrink={0}>
						<DataCollectionTableFooter />
					</Flex>
				</DataCollectionContextProvider>
			</Card.Body>
		</Card.Root>
	);
}
